import { adsApi } from '@/api/client';
import { formatCurrencyValue } from '@/api/keywordMetrics';
import type { KeywordAdProduct, KeywordEntityRow, UnifiedKeywordRow } from '@/api/unifiedKeywords';
import { coerceDisplayString } from '@/api/unifiedKeywords';

export type EditableKeywordBidTarget = {
  adProduct: KeywordAdProduct;
  keywordId: string;
  bid: number;
  campaignId?: string;
  adGroupId?: string;
  state?: string;
};

type KeywordMutationResult = {
  keywordId?: string | null;
  status?: string;
  message?: string;
};

function parseMutationResults(data: unknown): KeywordMutationResult[] {
  if (!data || typeof data !== 'object') return [];
  const raw = data as Record<string, unknown>;
  const results = raw.results;
  if (!Array.isArray(results)) return [];
  return results as KeywordMutationResult[];
}

function assertMutationSuccess(
  results: KeywordMutationResult[],
  keywordId: string
): void {
  const match =
    results.find((item) => item.keywordId && String(item.keywordId) === keywordId) ??
    results[0];
  if (!match) return;
  if (match.status === 'error') {
    throw new Error(match.message || 'Amazon rejected the bid update');
  }
}

export function resolveEditableKeywordBid(
  keyword: KeywordEntityRow,
  adProduct: KeywordAdProduct
): EditableKeywordBidTarget | null {
  const keywordId = coerceDisplayString(keyword.keyword_id);
  if (!keywordId) return null;
  if (typeof keyword.bid !== 'number' || !Number.isFinite(keyword.bid)) return null;

  if (adProduct === 'SP') {
    return {
      adProduct,
      keywordId,
      bid: keyword.bid,
    };
  }

  const campaignId = coerceDisplayString(keyword.campaign_id);
  const adGroupId = coerceDisplayString(keyword.ad_group_id);
  if (!campaignId || !adGroupId) return null;

  const state = coerceDisplayString(keyword.state).toLowerCase() || 'enabled';
  return {
    adProduct,
    keywordId,
    bid: keyword.bid,
    campaignId,
    adGroupId,
    state,
  };
}

/** Workspace entity-page rows (camelCase) → editable bid target. */
export function resolveEditableWorkspaceKeywordBid(row: {
  keywordId: string;
  bid?: number | null;
  adProduct: string;
  campaignId?: string;
  adGroupId?: string;
  state?: string;
  rowKind?: string;
}): EditableKeywordBidTarget | null {
  if (row.rowKind === 'auto_target') return null;
  if (!row.keywordId) return null;
  if (typeof row.bid !== 'number' || !Number.isFinite(row.bid)) return null;
  if (row.adProduct === 'SP') {
    return { adProduct: 'SP', keywordId: row.keywordId, bid: row.bid };
  }
  if (row.adProduct === 'SB') {
    if (!row.campaignId || !row.adGroupId) return null;
    return {
      adProduct: 'SB',
      keywordId: row.keywordId,
      bid: row.bid,
      campaignId: row.campaignId,
      adGroupId: row.adGroupId,
      state: (row.state || 'enabled').toLowerCase(),
    };
  }
  return null;
}

export function resolveEditableUnifiedKeywordBid(
  row: UnifiedKeywordRow
): EditableKeywordBidTarget | null {
  if (row.sources.length !== 1) return null;
  return resolveEditableKeywordBid(row.sources[0], row.sources[0].adProduct);
}

export async function applyKeywordBidUpdate(
  profileId: string,
  target: EditableKeywordBidTarget,
  newBid: number
): Promise<void> {
  if (!Number.isFinite(newBid) || newBid <= 0) {
    throw new Error('Enter a valid bid greater than 0');
  }

  if (target.adProduct === 'SP') {
    const res = await adsApi.updateSpKeywordBid(profileId, {
      keywordId: target.keywordId,
      bid: newBid,
    });
    if (!res.success) throw new Error('Failed to update bid');
    assertMutationSuccess(parseMutationResults(res.data), target.keywordId);
    return;
  }

  if (!target.campaignId || !target.adGroupId) {
    throw new Error('Missing campaign or ad group for SB keyword');
  }

  const res = await adsApi.updateSbKeywordBid(profileId, {
    keywordId: target.keywordId,
    campaignId: target.campaignId,
    adGroupId: target.adGroupId,
    bid: newBid,
    state: target.state || 'enabled',
  });
  if (!res.success) throw new Error('Failed to update bid');
  assertMutationSuccess(parseMutationResults(res.data), target.keywordId);
}

export function formatBidUpdateSuccessMessage(newBid: number): string {
  return `Bid updated to ${formatCurrencyValue(newBid)}`;
}
